import React, { useEffect, useState } from 'react';
import { RiNotification3Line, RiNotification3Fill, RiNotificationOffLine } from 'react-icons/ri';
import { useOffline } from './OfflineContext';
import { pushState, enablePush } from './push';
import styles from './PushOptIn.module.css';

// "Notify me when it's ready": shown next to a converting book. The
// permission prompt only fires from this tap (a browser rule, strict on
// iOS). On an iPhone in a Safari tab, push needs the installed app, so the
// tap opens the install tutorial instead.
export default function PushOptIn({ size = 28, compact = false }) {
  const offline = useOffline();
  const [state, setState] = useState(pushState);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const refresh = () => setState(pushState());
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, []);

  const stop = (event) => { event.preventDefault(); event.stopPropagation(); };

  if (state === 'unsupported') return null;

  // Once answered, collapse to a small ring so the conversion status
  // ("Recording cast…", chapter counts) beside it stays readable.
  if (state === 'granted') {
    return (
      <span
        className={`${styles.ring} ${styles.on}`}
        style={{ width: size, height: size }}
        role="img"
        aria-label="You'll get a notification when this book is ready"
        title="You'll get a notification when this book is ready"
      >
        <RiNotification3Fill size={size * 0.5} />
      </span>
    );
  }

  if (state === 'denied') {
    return (
      <span
        className={`${styles.ring} ${styles.off}`}
        style={{ width: size, height: size }}
        role="img"
        aria-label="Notifications are blocked for BYOPDF in your phone's settings"
        title="Notifications are blocked for BYOPDF in your phone's settings"
      >
        <RiNotificationOffLine size={size * 0.5} />
      </span>
    );
  }

  const onClick = async (event) => {
    stop(event);
    if (state === 'install-first') { offline.showInstallSheet('push'); return; }
    setBusy(true);
    try {
      setState(await enablePush());
    } catch (error) {
      console.error('Push opt-in failed:', error);
      setState(pushState());
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        className={`${styles.ring} ${styles.ringAsk} ${busy ? styles.ringBusy : ''}`}
        style={{ width: size, height: size }}
        onClick={onClick}
        disabled={busy}
        title="Notify me when this book is ready"
        aria-label="Notify me when this book is ready"
      >
        <RiNotification3Line size={size * 0.5} />
      </button>
    );
  }

  return (
    <button type="button" className={styles.btn} onClick={onClick} disabled={busy} title="Get a notification when this book is ready">
      <RiNotification3Line size={size * 0.5} />
      <span>{busy ? 'Setting up…' : 'Notify me'}</span>
    </button>
  );
}
